import { FormEvent, useEffect, useMemo, useState } from "react";
import { Button, Card, EmptyState, ErrorState, PageHeader, Skeleton } from "../components/ui";
import {
  ApiRequestError,
  acceptDietitianVisit,
  createVisit,
  fetchCreditsBalance,
  fetchDietitianVisitHistory,
  fetchDietitianPendingVisits,
  fetchVisits,
  updateVisit,
} from "../lib/api";
import { trackEvent } from "../lib/analytics";
import { useShellSession } from "../hooks/useShellSession";
import type { VisitRecord, VisitSlot } from "../types";

type VisitType = "VIDEO" | "IN_PERSON";
type VisitStatus = "REQUESTED" | "CONFIRMED" | "RESCHEDULED" | "CANCELED" | "COMPLETED";

interface VisitEditorDraft {
  status: VisitStatus;
  confirmedStartAt: string;
  confirmedEndAt: string;
  location: string;
  notes: string;
  zoomLink: string;
  meetingMetadata: string;
  cancellationReason: string;
}

interface MemberVisitDraft {
  preferredSlots: string[];
}

type DietitianWindow = "all" | "1d" | "1w" | "1y" | "month";
type DietitianStatusFilter = "ALL" | "REQUESTED" | "RESCHEDULED" | "CONFIRMED" | "COMPLETED" | "CANCELED";
type MemberLane = "FOCUS" | "UPCOMING" | "COMPLETED" | "CANCELED";
const STALE_PENDING_VISITS_STORAGE_KEY = "pantryrx.stale_pending_visits";

function formatDate(value?: string | null): string {
  if (!value) {
    return "TBD";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "TBD";
  }
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(parsed);
}

function toLocalInput(value?: string | null): string {
  if (!value) {
    return "";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }
  const local = new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function toIso(value: string): string | null {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed.toISOString();
}

function toStatusLabel(status?: string | null): string {
  const source = (status || "").trim();
  if (!source) {
    return "REQUESTED";
  }
  return source.replace(/_/g, " ").toUpperCase();
}

function toNormalizedVisitStatus(status?: string | null): VisitStatus {
  const normalized = (status || "").toUpperCase();
  if (normalized === "CONFIRMED") {
    return "CONFIRMED";
  }
  if (normalized === "RESCHEDULED") {
    return "RESCHEDULED";
  }
  if (normalized === "CANCELED") {
    return "CANCELED";
  }
  if (normalized === "COMPLETED") {
    return "COMPLETED";
  }
  return "REQUESTED";
}

function toStatusBadgeClass(status?: string | null): string {
  const normalized = toNormalizedVisitStatus(status);
  if (normalized === "CONFIRMED") {
    return "border-emerald-300 bg-emerald-50 text-emerald-700";
  }
  if (normalized === "COMPLETED") {
    return "border-teal-300 bg-teal-50 text-teal-700";
  }
  if (normalized === "CANCELED") {
    return "border-rose-300 bg-rose-50 text-rose-700";
  }
  if (normalized === "RESCHEDULED") {
    return "border-sky-300 bg-sky-50 text-sky-700";
  }
  return "border-amber-300 bg-amber-50 text-amber-700";
}

function toStatusGroupLabel(filter: DietitianStatusFilter): string {
  if (filter === "ALL") {
    return "All";
  }
  return toStatusLabel(filter);
}

function toFirstName(value?: string | null): string {
  const source = (value || "").trim();
  if (!source) {
    return "Dietitian";
  }
  const [first] = source.split(/\s+/);
  return first || "Dietitian";
}

function toCreditNumber(value: unknown): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function sortVisits(items: VisitRecord[]): VisitRecord[] {
  return [...items].sort((left, right) => {
    const leftTime = new Date(
      left.confirmed_start_at || left.requested_start_at || left.created_at || "",
    ).getTime();
    const rightTime = new Date(
      right.confirmed_start_at || right.requested_start_at || right.created_at || "",
    ).getTime();
    return rightTime - leftTime;
  });
}

function buildDraft(visit: VisitRecord): VisitEditorDraft {
  const normalizedStatus = (visit.status || "REQUESTED").toUpperCase();
  const status: VisitStatus = (
    ["REQUESTED", "CONFIRMED", "RESCHEDULED", "CANCELED", "COMPLETED"].includes(normalizedStatus)
      ? normalizedStatus
      : "REQUESTED"
  ) as VisitStatus;

  return {
    status,
    confirmedStartAt: toLocalInput(visit.confirmed_start_at || visit.requested_start_at),
    confirmedEndAt: toLocalInput(visit.confirmed_end_at || visit.requested_end_at),
    location: visit.location || "",
    notes: visit.notes || "",
    zoomLink: visit.meeting_url || "",
    meetingMetadata: visit.meeting_metadata || "",
    cancellationReason: visit.cancellation_reason || "",
  };
}

function buildMemberDraft(visit: VisitRecord): MemberVisitDraft {
  const slots = Array.isArray(visit.preferred_slots) ? visit.preferred_slots : [];
  const preferredSlots = Array.from({ length: 5 }, (_, index) => toLocalInput(slots[index]?.start_at || null));
  return {
    preferredSlots,
  };
}

function withSlotFallback(slot?: VisitSlot | null): { start: string | null; end: string | null } {
  if (!slot) {
    return { start: null, end: null };
  }
  return {
    start: slot.start_at || null,
    end: slot.end_at || null,
  };
}

function buildPreferredSlots(values: string[]): VisitSlot[] | null {
  const starts = values
    .map(toIso)
    .filter((entry): entry is string => Boolean(entry))
    .slice(0, 5);
  if (starts.length < 3) {
    return null;
  }
  return starts.map((startAt) => {
    const start = new Date(startAt);
    const end = new Date(start.getTime() + 45 * 60 * 1000);
    return {
      start_at: start.toISOString(),
      end_at: end.toISOString(),
    };
  });
}

function buildWindowRange(window: DietitianWindow, monthValue: string): { from: string | null; to: string | null } {
  if (window === "all") {
    return { from: null, to: null };
  }
  const now = new Date();
  if (window === "month" && monthValue) {
    const [yearRaw, monthRaw] = monthValue.split("-");
    const year = Number(yearRaw);
    const month = Number(monthRaw);
    if (Number.isFinite(year) && Number.isFinite(month) && month >= 1 && month <= 12) {
      const from = new Date(Date.UTC(year, month - 1, 1, 0, 0, 0));
      const to = new Date(Date.UTC(year, month, 0, 23, 59, 59));
      return { from: from.toISOString(), to: to.toISOString() };
    }
  }
  const from = new Date(now);
  if (window === "1d") {
    from.setDate(from.getDate() - 1);
  } else if (window === "1w") {
    from.setDate(from.getDate() - 7);
  } else {
    from.setFullYear(from.getFullYear() - 1);
  }
  return { from: from.toISOString(), to: now.toISOString() };
}

function includesAlreadyAcceptedText(value: unknown): boolean {
  return typeof value === "string" && /already accepted/i.test(value);
}

function isVisitAlreadyAcceptedError(error: unknown): boolean {
  if (error instanceof ApiRequestError) {
    if (includesAlreadyAcceptedText(error.message)) {
      return true;
    }
    if (error.payload && typeof error.payload === "object") {
      const payload = error.payload as Record<string, unknown>;
      if (includesAlreadyAcceptedText(payload.detail) || includesAlreadyAcceptedText(payload.message)) {
        return true;
      }
      if (payload.payload && typeof payload.payload === "object") {
        const nested = payload.payload as Record<string, unknown>;
        if (includesAlreadyAcceptedText(nested.message) || includesAlreadyAcceptedText(nested.detail)) {
          return true;
        }
      }
    }
  }
  if (error instanceof Error) {
    return includesAlreadyAcceptedText(error.message);
  }
  return false;
}

function loadStalePendingVisitsFromStorage(): Record<string, true> {
  if (typeof window === "undefined") {
    return {};
  }
  try {
    const raw = window.localStorage.getItem(STALE_PENDING_VISITS_STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return {};
    }
    const next: Record<string, true> = {};
    for (const item of parsed) {
      if (typeof item === "string" && item.trim()) {
        next[item] = true;
      }
    }
    return next;
  } catch {
    return {};
  }
}

function saveStalePendingVisitsToStorage(stalePendingVisitIds: Record<string, true>): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(
      STALE_PENDING_VISITS_STORAGE_KEY,
      JSON.stringify(Object.keys(stalePendingVisitIds)),
    );
  } catch {
    // Ignore storage failures.
  }
}

function toErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiRequestError) {
    const payloadDetail =
      error.payload && typeof error.payload === "object" && typeof error.payload.detail === "string"
        ? error.payload.detail
        : null;
    return payloadDetail || error.message || fallback;
  }
  if (error instanceof Error) {
    return error.message || fallback;
  }
  return fallback;
}

function toDebugJson(value: unknown): string {
  if (value === null || value === undefined) {
    return "null";
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function readRequestId(payload: unknown): string | null {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const source = payload as Record<string, unknown>;
  const requestIdKeys = ["request_id", "requestId", "trace_id", "traceId", "correlation_id", "correlationId"];
  for (const key of requestIdKeys) {
    const candidate = source[key];
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }

  const nestedPayload = source.payload;
  if (!nestedPayload || typeof nestedPayload !== "object") {
    return null;
  }

  const nested = nestedPayload as Record<string, unknown>;
  for (const key of requestIdKeys) {
    const candidate = nested[key];
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }

  return null;
}

function buildVisitActionDebugMessage(params: {
  action: "accept" | "cancel" | "reject_slot";
  visit: VisitRecord;
  selectedIndex: number | null;
  attemptedSlotIndices?: number[];
  endpoint: string;
  error: unknown;
}): string {
  const slots = Array.isArray(params.visit.preferred_slots) ? params.visit.preferred_slots : [];
  const selectedSlot =
    typeof params.selectedIndex === "number" && params.selectedIndex >= 0 ? slots[params.selectedIndex] ?? null : null;
  const attemptedSlots = Array.isArray(params.attemptedSlotIndices) ? params.attemptedSlotIndices : [];

  const lines: string[] = [];
  lines.push("PantryRx visit triage debug");
  lines.push(`timestamp_utc=${new Date().toISOString()}`);
  lines.push(`action=${params.action}`);
  lines.push(`visit_id=${params.visit.id}`);
  lines.push(`visit_status=${params.visit.status || "UNKNOWN"}`);
  lines.push(`selected_slot_index=${params.selectedIndex === null ? "n/a" : String(params.selectedIndex)}`);
  lines.push(
    `selected_slot_start=${selectedSlot?.start_at || "n/a"} selected_slot_end=${selectedSlot?.end_at || "n/a"}`,
  );
  lines.push(`attempted_slot_indices=${attemptedSlots.length > 0 ? attemptedSlots.join(",") : "n/a"}`);
  lines.push(`preferred_slot_count=${String(slots.length)}`);
  lines.push(`endpoint=${params.endpoint}`);

  if (params.error instanceof ApiRequestError) {
    const payloadDetail =
      params.error.payload && typeof params.error.payload === "object" && typeof params.error.payload.detail === "string"
        ? params.error.payload.detail
        : null;
    const requestId = readRequestId(params.error.payload);
    lines.push(`http_status=${params.error.status}`);
    lines.push(`api_message=${params.error.message || "n/a"}`);
    lines.push(`api_detail=${payloadDetail || "n/a"}`);
    lines.push(`request_id=${requestId || "n/a"}`);
    lines.push(`api_payload=${toDebugJson(params.error.payload)}`);
    return lines.join("\n");
  }

  if (params.error instanceof Error) {
    lines.push("http_status=n/a");
    lines.push(`error_name=${params.error.name || "Error"}`);
    lines.push(`error_message=${params.error.message || "n/a"}`);
    if (params.error.stack) {
      lines.push(`stack=${params.error.stack}`);
    }
    return lines.join("\n");
  }

  lines.push("http_status=n/a");
  lines.push(`error=${toDebugJson(params.error)}`);
  return lines.join("\n");
}

function buildAcceptanceHistoryText(params: {
  visit: VisitRecord;
  currentDietitianId: string | null;
  currentDietitianFirstName: string;
}): string | null {
  const status = toNormalizedVisitStatus(params.visit.status);
  const hasAcceptanceMarker =
    status === "CONFIRMED" ||
    (typeof params.visit.accepted_slot_index === "number" && params.visit.accepted_slot_index >= 0) ||
    Boolean(params.visit.accepted_by_dietitian_id);
  if (!hasAcceptanceMarker) {
    return null;
  }

  const acceptedById = (params.visit.accepted_by_dietitian_id || "").trim();
  let acceptedByLabel = params.currentDietitianFirstName;
  if (acceptedById && params.currentDietitianId && acceptedById !== params.currentDietitianId) {
    acceptedByLabel = `Dietitian ${acceptedById.slice(0, 8)}`;
  }

  const acceptedAt =
    params.visit.updated_at || params.visit.confirmed_start_at || params.visit.created_at || params.visit.requested_start_at;
  if (acceptedAt) {
    return `Accepted by ${acceptedByLabel} on ${formatDate(acceptedAt)}.`;
  }
  return `Accepted by ${acceptedByLabel}.`;
}

function isActionablePendingVisit(visit: VisitRecord): boolean {
  const normalizedStatus = (visit.status || "").toUpperCase();
  if (normalizedStatus && normalizedStatus !== "REQUESTED" && normalizedStatus !== "RESCHEDULED") {
    return false;
  }
  if (
    typeof visit.accepted_slot_index === "number" &&
    Number.isFinite(visit.accepted_slot_index) &&
    visit.accepted_slot_index >= 0
  ) {
    return false;
  }
  if (visit.accepted_by_dietitian_id) {
    return false;
  }
  if (visit.confirmed_start_at || visit.confirmed_end_at) {
    return false;
  }
  return true;
}

export function VisitsPage() {
  const { session } = useShellSession();
  const isDietitian = Boolean(session.user?.isDietitian);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [debugMessage, setDebugMessage] = useState<string | null>(null);
  const [debugCopied, setDebugCopied] = useState(false);
  const [visits, setVisits] = useState<VisitRecord[]>([]);
  const [pendingVisits, setPendingVisits] = useState<VisitRecord[]>([]);
  const [stalePendingVisitIds, setStalePendingVisitIds] = useState<Record<string, true>>(() =>
    loadStalePendingVisitsFromStorage(),
  );
  const [showHiddenPending, setShowHiddenPending] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, VisitEditorDraft>>({});
  const [memberDrafts, setMemberDrafts] = useState<Record<string, MemberVisitDraft>>({});
  const [dietitianCredits, setDietitianCredits] = useState<number | null>(null);
  const [scanCredits, setScanCredits] = useState<number | null>(null);
  const [memberLane, setMemberLane] = useState<MemberLane>("FOCUS");
  const [memberDeckIndex, setMemberDeckIndex] = useState(0);
  const [expandedMemberActions, setExpandedMemberActions] = useState<Record<string, true>>({});
  const [selectedPendingSlot, setSelectedPendingSlot] = useState<Record<string, number>>({});
  const [updatingVisitId, setUpdatingVisitId] = useState<string | null>(null);
  const [requesting, setRequesting] = useState(false);
  const [showMemberScheduler, setShowMemberScheduler] = useState(false);
  const [filterWindow, setFilterWindow] = useState<DietitianWindow>("all");
  const [dietitianStatusFilter, setDietitianStatusFilter] = useState<DietitianStatusFilter>("ALL");
  const [dietitianSearch, setDietitianSearch] = useState("");
  const [filterMonth, setFilterMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });

  const [visitType, setVisitType] = useState<VisitType>("VIDEO");
  const [startAt, setStartAt] = useState("");
  const [endAt, setEndAt] = useState("");
  const [preferredSlot1, setPreferredSlot1] = useState("");
  const [preferredSlot2, setPreferredSlot2] = useState("");
  const [preferredSlot3, setPreferredSlot3] = useState("");
  const [preferredSlot4, setPreferredSlot4] = useState("");
  const [preferredSlot5, setPreferredSlot5] = useState("");
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  const currentDietitianId = session.user?.id ? String(session.user.id) : null;
  const currentDietitianFirstName = toFirstName(session.user?.name);
  const canMemberManageAppointments = isDietitian || dietitianCredits === null || dietitianCredits > 0;

  async function load(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const { from, to } = buildWindowRange(filterWindow, filterMonth);
      const [visitList, pendingList, creditsPayload] = await Promise.all([
        isDietitian
          ? fetchDietitianVisitHistory({ from, to, limit: 100, offset: 0 })
          : fetchVisits({ limit: 100, offset: 0 }),
        isDietitian ? fetchDietitianPendingVisits({ limit: 100, offset: 0, from, to }) : Promise.resolve([]),
        isDietitian ? Promise.resolve(null) : fetchCreditsBalance().catch(() => null),
      ]);
      const sortedVisits = sortVisits(visitList);
      const sortedPendingVisits = sortVisits(pendingList);
      setVisits(sortedVisits);
      setPendingVisits(sortedPendingVisits);
      if (!isDietitian) {
        setDietitianCredits(toCreditNumber(creditsPayload?.dietitian_credits ?? creditsPayload?.consult_credits));
        setScanCredits(toCreditNumber(creditsPayload?.scan_credits ?? creditsPayload?.credits_available));
      }
      setStalePendingVisitIds((current) => {
        const pendingIds = new Set(sortedPendingVisits.map((visit) => visit.id));
        const next: Record<string, true> = {};
        for (const visitId of Object.keys(current)) {
          if (pendingIds.has(visitId)) {
            next[visitId] = true;
          }
        }
        return next;
      });
      setDrafts((current) => {
        const next: Record<string, VisitEditorDraft> = {};
        for (const visit of sortedVisits) {
          next[visit.id] = current[visit.id] ?? buildDraft(visit);
        }
        return next;
      });
      setMemberDrafts((current) => {
        const next: Record<string, MemberVisitDraft> = {};
        for (const visit of sortedVisits) {
          next[visit.id] = current[visit.id] ?? buildMemberDraft(visit);
        }
        return next;
      });
      setSelectedPendingSlot((current) => {
        const next: Record<string, number> = { ...current };
        for (const visit of pendingList) {
          if (typeof next[visit.id] !== "number") {
            next[visit.id] = visit.accepted_slot_index ?? 0;
          }
        }
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load appointment requests.");
    } finally {
      setLoading(false);
      setUpdatingVisitId(null);
    }
  }

  async function handleCopyDebugMessage(): Promise<void> {
    if (!debugMessage) {
      return;
    }
    try {
      await navigator.clipboard.writeText(debugMessage);
      setDebugCopied(true);
      window.setTimeout(() => {
        setDebugCopied(false);
      }, 2000);
    } catch {
      setError("Unable to copy debug details automatically. Select and copy the debug text manually.");
    }
  }

  useEffect(() => {
    void load();
  }, [filterMonth, filterWindow, isDietitian]);

  useEffect(() => {
    saveStalePendingVisitsToStorage(stalePendingVisitIds);
  }, [stalePendingVisitIds]);

  async function handleCreateRequest(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (isDietitian || requesting) {
      return;
    }
    if (!canMemberManageAppointments) {
      setError("You need at least 1 dietitian credit to book or manage appointments.");
      return;
    }
    const preferredSlots = buildPreferredSlots([
      preferredSlot1,
      preferredSlot2,
      preferredSlot3,
      preferredSlot4,
      preferredSlot5,
    ]);
    if (!preferredSlots || preferredSlots.length < 3) {
      setError("Please provide at least 3 preferred time slots.");
      return;
    }
    setRequesting(true);
    setError(null);
    try {
      await createVisit({
        type: visitType,
        requested_start_at: toIso(startAt),
        requested_end_at: toIso(endAt),
        preferred_slots: preferredSlots,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
        location: location.trim() || null,
        notes: notes.trim() || null,
      });
      trackEvent("visit_request_created", {
        visit_type: visitType.toLowerCase(),
        preferred_slot_count: preferredSlots.length,
        has_location: Boolean(location.trim()),
        has_notes: Boolean(notes.trim()),
      });
      setStartAt("");
      setEndAt("");
      setPreferredSlot1("");
      setPreferredSlot2("");
      setPreferredSlot3("");
      setPreferredSlot4("");
      setPreferredSlot5("");
      setLocation("");
      setNotes("");
      setShowMemberScheduler(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to submit appointment request.");
      setRequesting(false);
    } finally {
      setRequesting(false);
    }
  }

  function patchDraft(visitId: string, patch: Partial<VisitEditorDraft>): void {
    setDrafts((current) => {
      const next = { ...current };
      next[visitId] = {
        ...(current[visitId] ?? {
          status: "REQUESTED",
          confirmedStartAt: "",
          confirmedEndAt: "",
          location: "",
          notes: "",
          zoomLink: "",
          meetingMetadata: "",
          cancellationReason: "",
        }),
        ...patch,
      };
      return next;
    });
  }

  function patchMemberDraft(visitId: string, slotIndex: number, value: string): void {
    setMemberDrafts((current) => {
      const existing = current[visitId] ?? { preferredSlots: ["", "", "", "", ""] };
      const preferredSlots = [...existing.preferredSlots];
      while (preferredSlots.length < 5) {
        preferredSlots.push("");
      }
      preferredSlots[slotIndex] = value;
      return {
        ...current,
        [visitId]: {
          preferredSlots,
        },
      };
    });
  }

  function toggleMemberActions(visitId: string): void {
    setExpandedMemberActions((current) => {
      if (current[visitId]) {
        const next = { ...current };
        delete next[visitId];
        return next;
      }
      return { ...current, [visitId]: true };
    });
  }

  function suppressPendingVisit(visitId: string): void {
    if (!visitId) {
      return;
    }
    setPendingVisits((current) => current.filter((visit) => visit.id !== visitId));
    setShowHiddenPending(false);
    setStalePendingVisitIds((current) => ({
      ...current,
      [visitId]: true,
    }));
  }

  function resetHiddenPendingVisits(): void {
    setStalePendingVisitIds({});
    setShowHiddenPending(false);
  }

  async function handleSaveVisit(visit: VisitRecord): Promise<void> {
    if (!isDietitian || updatingVisitId) {
      return;
    }
    if (toNormalizedVisitStatus(visit.status) === "COMPLETED") {
      setError("Completed appointments are read-only and cannot be changed.");
      return;
    }
    const draft = drafts[visit.id];
    if (!draft) {
      return;
    }
    setUpdatingVisitId(visit.id);
    setError(null);
    try {
      await updateVisit(visit.id, {
        status: draft.status,
        confirmed_start_at: toIso(draft.confirmedStartAt),
        confirmed_end_at: toIso(draft.confirmedEndAt),
        timezone: visit.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
        location: draft.location.trim() || null,
        notes: draft.notes.trim() || null,
        meeting_url: draft.zoomLink.trim() || null,
        meeting_metadata: draft.meetingMetadata.trim() || null,
        cancellation_reason:
          draft.status === "CANCELED" ? draft.cancellationReason.trim() || "Canceled by dietitian" : null,
      });
      trackEvent("visit_updated_by_dietitian", { status: draft.status.toLowerCase() });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to update appointment.");
      setUpdatingVisitId(null);
    }
  }

  async function handleAcceptPending(visit: VisitRecord): Promise<void> {
    if (!isDietitian || updatingVisitId) {
      return;
    }
    const selectedIndex = selectedPendingSlot[visit.id] ?? 0;
    const attemptedIndices: number[] = [];
    setUpdatingVisitId(visit.id);
    setError(null);
    setDebugMessage(null);
    setDebugCopied(false);
    try {
      attemptedIndices.push(selectedIndex);
      await acceptDietitianVisit(visit.id, selectedIndex);
      trackEvent("visit_pending_accepted", { selected_slot_index: selectedIndex + 1, used_alternate_slot: false });
      await load();
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 409) {
        const firstAttemptDebug = buildVisitActionDebugMessage({
          action: "accept",
          visit,
          selectedIndex,
          attemptedSlotIndices: attemptedIndices,
          endpoint: `/app/api/v1/dietitian/visits/${visit.id}/accept`,
          error: err,
        });
        if (isVisitAlreadyAcceptedError(err)) {
          setDebugMessage(firstAttemptDebug);
          suppressPendingVisit(visit.id);
          await load();
          setError("This request was already accepted by another dietitian and is now hidden from pending.");
          setUpdatingVisitId(null);
          return;
        }

        const slots = Array.isArray(visit.preferred_slots) ? visit.preferred_slots : [];
        const alternateIndices = slots
          .map((_, idx) => idx)
          .filter((slotIndex) => slotIndex !== selectedIndex);
        for (const altIndex of alternateIndices) {
          try {
            attemptedIndices.push(altIndex);
            await acceptDietitianVisit(visit.id, altIndex);
            setSelectedPendingSlot((current) => ({ ...current, [visit.id]: altIndex }));
            trackEvent("visit_pending_accepted", { selected_slot_index: altIndex + 1, used_alternate_slot: true });
            await load();
            setError("Requested slot was unavailable. Accepted the next available preferred slot.");
            return;
          } catch (alternateErr) {
            if (alternateErr instanceof ApiRequestError && alternateErr.status === 409) {
              if (isVisitAlreadyAcceptedError(alternateErr)) {
                const nextDebugMessage = buildVisitActionDebugMessage({
                  action: "accept",
                  visit,
                  selectedIndex,
                  attemptedSlotIndices: attemptedIndices,
                  endpoint: `/app/api/v1/dietitian/visits/${visit.id}/accept`,
                  error: alternateErr,
                });
                setDebugMessage(nextDebugMessage);
                suppressPendingVisit(visit.id);
                await load();
                setError("This request was already accepted by another dietitian and is now hidden from pending.");
                setUpdatingVisitId(null);
                return;
              }
              continue;
            }

            {
              const nextDebugMessage = buildVisitActionDebugMessage({
                action: "accept",
                visit,
                selectedIndex,
                attemptedSlotIndices: attemptedIndices,
                endpoint: `/app/api/v1/dietitian/visits/${visit.id}/accept`,
                error: alternateErr,
              });
              setDebugMessage(nextDebugMessage);
              if (isVisitAlreadyAcceptedError(alternateErr)) {
                suppressPendingVisit(visit.id);
                await load();
                setError("This request was already accepted by another dietitian and is now hidden from pending.");
                setUpdatingVisitId(null);
                return;
              }
              await load();
              setError(toErrorMessage(alternateErr, "Unable to accept request."));
              setUpdatingVisitId(null);
              return;
            }
          }
        }
        const nextDebugMessage = buildVisitActionDebugMessage({
          action: "accept",
          visit,
          selectedIndex,
          attemptedSlotIndices: attemptedIndices,
          endpoint: `/app/api/v1/dietitian/visits/${visit.id}/accept`,
          error: err,
        });
        setDebugMessage(nextDebugMessage);
        if (isVisitAlreadyAcceptedError(err)) {
          suppressPendingVisit(visit.id);
          await load();
          setError("This request was already accepted by another dietitian and is now hidden from pending.");
          setUpdatingVisitId(null);
          return;
        }
        await load();
        setError(toErrorMessage(err, "Unable to accept request."));
        setUpdatingVisitId(null);
        return;
      }
      if (isVisitAlreadyAcceptedError(err)) {
        suppressPendingVisit(visit.id);
        await load();
        setError("This request was already accepted by another dietitian and is now hidden from pending.");
        setUpdatingVisitId(null);
        return;
      }
      setDebugMessage(
        buildVisitActionDebugMessage({
          action: "accept",
          visit,
          selectedIndex,
          attemptedSlotIndices: attemptedIndices.length > 0 ? attemptedIndices : [selectedIndex],
          endpoint: `/app/api/v1/dietitian/visits/${visit.id}/accept`,
          error: err,
        }),
      );
      setError(toErrorMessage(err, "Unable to accept request."));
      setUpdatingVisitId(null);
    }
  }

  async function handleCancelPending(visit: VisitRecord): Promise<void> {
    if (!isDietitian || updatingVisitId) {
      return;
    }
    setUpdatingVisitId(visit.id);
    setError(null);
    setDebugMessage(null);
    setDebugCopied(false);
    try {
      await updateVisit(visit.id, {
        status: "CANCELED",
        cancellation_reason: "Canceled by dietitian",
        notes: visit.notes || null,
        location: visit.location || null,
      });
      trackEvent("visit_pending_canceled", { actor: "dietitian" });
      await load();
    } catch (err) {
      setDebugMessage(
        buildVisitActionDebugMessage({
          action: "cancel",
          visit,
          selectedIndex: selectedPendingSlot[visit.id] ?? null,
          endpoint: `/app/api/v1/visits/${visit.id}`,
          error: err,
        }),
      );
      if (err instanceof ApiRequestError && err.status === 404) {
        await load();
        setError("This request type cannot be canceled through the current API endpoint.");
        setUpdatingVisitId(null);
        return;
      }
      if (isVisitAlreadyAcceptedError(err)) {
        await load();
      }
      setError(toErrorMessage(err, "Unable to cancel request."));
      setUpdatingVisitId(null);
    }
  }

  async function handleRejectPendingSlot(visit: VisitRecord): Promise<void> {
    if (!isDietitian || updatingVisitId) {
      return;
    }
    const slots = Array.isArray(visit.preferred_slots) ? visit.preferred_slots : [];
    const selectedIndex = selectedPendingSlot[visit.id] ?? 0;
    if (!slots[selectedIndex]) {
      setError("Choose a requested slot to reject.");
      return;
    }
    const remainingSlots = slots.filter((_, slotIndex) => slotIndex !== selectedIndex);
    const validRemainingSlots = remainingSlots.length >= 3 ? remainingSlots : null;
    setUpdatingVisitId(visit.id);
    setError(null);
    setDebugMessage(null);
    setDebugCopied(false);
    try {
      await updateVisit(visit.id, {
        status: validRemainingSlots ? "RESCHEDULED" : "CANCELED",
        preferred_slots: validRemainingSlots,
        cancellation_reason: validRemainingSlots ? null : "All requested slots rejected by dietitian.",
        notes: visit.notes || null,
        location: visit.location || null,
      });
      trackEvent("visit_pending_rejected", {
        remaining_slot_count: validRemainingSlots ? validRemainingSlots.length : 0,
        outcome: validRemainingSlots ? "rescheduled" : "canceled",
      });
      await load();
    } catch (err) {
      setDebugMessage(
        buildVisitActionDebugMessage({
          action: "reject_slot",
          visit,
          selectedIndex,
          attemptedSlotIndices: [selectedIndex],
          endpoint: `/app/api/v1/visits/${visit.id}`,
          error: err,
        }),
      );
      if (err instanceof ApiRequestError && err.status === 404) {
        await load();
        setError("This request type cannot be rejected through the current API endpoint.");
        setUpdatingVisitId(null);
        return;
      }
      if (isVisitAlreadyAcceptedError(err)) {
        await load();
      }
      setError(toErrorMessage(err, "Unable to reject slot."));
      setUpdatingVisitId(null);
    }
  }

  async function handleMemberReschedule(visit: VisitRecord): Promise<void> {
    if (isDietitian || updatingVisitId) {
      return;
    }
    if (!canMemberManageAppointments) {
      setError("You need at least 1 dietitian credit to reschedule appointments.");
      return;
    }
    const draft = memberDrafts[visit.id] ?? buildMemberDraft(visit);
    const preferredSlots = buildPreferredSlots(draft.preferredSlots);
    if (!preferredSlots || preferredSlots.length < 3) {
      setError("Please provide at least 3 preferred time slots to reschedule.");
      return;
    }
    setUpdatingVisitId(visit.id);
    setError(null);
    try {
      await updateVisit(visit.id, {
        status: "RESCHEDULED",
        requested_start_at: preferredSlots[0]?.start_at ?? null,
        requested_end_at: preferredSlots[0]?.end_at ?? null,
        preferred_slots: preferredSlots,
        confirmed_start_at: null,
        confirmed_end_at: null,
        notes: visit.notes || null,
        location: visit.location || null,
      });
      trackEvent("visit_rescheduled", { actor: "member", preferred_slot_count: preferredSlots.length });
      await load();
    } catch (err) {
      setError(toErrorMessage(err, "Unable to reschedule appointment."));
      setUpdatingVisitId(null);
    }
  }

  async function handleMemberCancel(visit: VisitRecord): Promise<void> {
    if (isDietitian || updatingVisitId) {
      return;
    }
    if (!canMemberManageAppointments) {
      setError("You need at least 1 dietitian credit to manage appointments.");
      return;
    }
    setUpdatingVisitId(visit.id);
    setError(null);
    try {
      await updateVisit(visit.id, {
        status: "CANCELED",
        cancellation_reason: "Canceled by member",
        notes: visit.notes || null,
        location: visit.location || null,
      });
      trackEvent("visit_canceled", { actor: "member" });
      await load();
    } catch (err) {
      setError(toErrorMessage(err, "Unable to cancel appointment."));
      setUpdatingVisitId(null);
    }
  }

  const hiddenPendingCount = useMemo(
    () => pendingVisits.filter((visit) => stalePendingVisitIds[visit.id]).length,
    [pendingVisits, stalePendingVisitIds],
  );

  const actionablePendingVisits = useMemo(
    () =>
      pendingVisits
        .filter((visit) => showHiddenPending || !stalePendingVisitIds[visit.id])
        .filter((visit) => isActionablePendingVisit(visit)),
    [pendingVisits, showHiddenPending, stalePendingVisitIds],
  );

  const memberVisits = useMemo(() => {
    if (isDietitian) {
      return visits;
    }
    // /v1/visits is already scoped to the authenticated member by API.
    // Avoid client-side user-id matching because identifier formats can differ by backend.
    return visits;
  }, [isDietitian, visits]);

  const dietitianStatusCounts = useMemo(() => {
    const counts: Record<DietitianStatusFilter, number> = {
      ALL: memberVisits.length,
      REQUESTED: 0,
      RESCHEDULED: 0,
      CONFIRMED: 0,
      COMPLETED: 0,
      CANCELED: 0,
    };
    for (const visit of memberVisits) {
      const normalized = toNormalizedVisitStatus(visit.status);
      counts[normalized] += 1;
    }
    return counts;
  }, [memberVisits]);

  const dietitianManagedVisits = useMemo(() => {
    const filteredByStatus =
      dietitianStatusFilter === "ALL"
        ? memberVisits
        : memberVisits.filter((visit) => toNormalizedVisitStatus(visit.status) === dietitianStatusFilter);

    const query = dietitianSearch.trim().toLowerCase();
    if (!query) {
      return filteredByStatus;
    }

    return filteredByStatus.filter((visit) =>
      [
        visit.id,
        visit.user_id,
        visit.dietitian_id,
        visit.type,
        visit.status,
        visit.location,
        visit.notes,
        visit.meeting_metadata,
        visit.cancellation_reason,
      ]
        .filter((value): value is string => typeof value === "string")
        .some((value) => value.toLowerCase().includes(query)),
    );
  }, [dietitianSearch, dietitianStatusFilter, memberVisits]);

  const memberFocusVisits = useMemo(
    () =>
      memberVisits.filter((visit) => {
        const status = toNormalizedVisitStatus(visit.status);
        return status === "REQUESTED" || status === "RESCHEDULED" || status === "CONFIRMED";
      }),
    [memberVisits],
  );

  const memberUpcomingVisits = useMemo(
    () => memberVisits.filter((visit) => toNormalizedVisitStatus(visit.status) === "CONFIRMED"),
    [memberVisits],
  );

  const memberCompletedVisits = useMemo(
    () => memberVisits.filter((visit) => toNormalizedVisitStatus(visit.status) === "COMPLETED"),
    [memberVisits],
  );

  const memberCanceledVisits = useMemo(
    () => memberVisits.filter((visit) => toNormalizedVisitStatus(visit.status) === "CANCELED"),
    [memberVisits],
  );

  const memberLaneVisits = useMemo(() => {
    if (memberLane === "UPCOMING") {
      return memberUpcomingVisits;
    }
    if (memberLane === "COMPLETED") {
      return memberCompletedVisits;
    }
    if (memberLane === "CANCELED") {
      return memberCanceledVisits;
    }
    return memberFocusVisits;
  }, [memberCanceledVisits, memberCompletedVisits, memberFocusVisits, memberLane, memberUpcomingVisits]);

  const activeMemberVisit = memberLaneVisits[memberDeckIndex] ?? null;

  useEffect(() => {
    if (memberLaneVisits.length === 0) {
      if (memberDeckIndex !== 0) {
        setMemberDeckIndex(0);
      }
      return;
    }
    if (memberDeckIndex >= memberLaneVisits.length) {
      setMemberDeckIndex(memberLaneVisits.length - 1);
    }
  }, [memberDeckIndex, memberLaneVisits]);

  const activeMemberStatus = activeMemberVisit ? toNormalizedVisitStatus(activeMemberVisit.status) : null;
  const activeMemberDraft = activeMemberVisit ? (memberDrafts[activeMemberVisit.id] ?? buildMemberDraft(activeMemberVisit)) : null;
  const activeMemberIsReadOnly = activeMemberStatus === "CANCELED" || activeMemberStatus === "COMPLETED";
  const activeMemberIsMutating = activeMemberVisit ? updatingVisitId === activeMemberVisit.id : false;
  const activeMemberActionsExpanded = activeMemberVisit ? Boolean(expandedMemberActions[activeMemberVisit.id]) : false;
  const memberArchiveVisits = useMemo(
    () =>
      memberVisits.filter((visit) => {
        const status = toNormalizedVisitStatus(visit.status);
        return status === "COMPLETED" || status === "CANCELED";
      }),
    [memberVisits],
  );

  return (
    <div data-testid="visits-page" className="space-y-4 pb-4">
      <PageHeader
        title="Visits"
        subtitle={
          isDietitian
            ? "Triaging visit requests across members, including slot acceptance, status control, and care notes."
            : "Request appointments with your dietitian and track visit status."
        }
      />

      {isDietitian ? (
        <Card>
          <div className="grid gap-3 md:grid-cols-[minmax(0,420px)_220px]">
            <label className="flex flex-col gap-2 text-sm">
              <span className="font-medium text-[var(--pr-text-muted)]">Filter requests</span>
              <select
                value={filterWindow}
                onChange={(event) => setFilterWindow((event.target.value as DietitianWindow) || "all")}
                className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2"
              >
                <option value="all">All time</option>
                <option value="1d">Past 1 Day</option>
                <option value="1w">Past 1 Week</option>
                <option value="1y">Past 1 Year</option>
                <option value="month">Specific Month</option>
              </select>
            </label>
            <label className="flex flex-col gap-2 text-sm">
              <span className="font-medium text-[var(--pr-text-muted)]">Month</span>
              <input
                type="month"
                value={filterMonth}
                onChange={(event) => setFilterMonth(event.target.value)}
                disabled={filterWindow !== "month"}
                className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2 disabled:opacity-60"
              />
            </label>
          </div>
        </Card>
      ) : null}

      {error ? (
        <div className="space-y-3">
          <ErrorState message={error} onRetry={() => void load()} />
          {debugMessage ? (
            <Card flat className="border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)]">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-semibold">Debug details</h3>
                <Button variant="secondary" className="min-h-10 px-3 text-xs" onClick={() => void handleCopyDebugMessage()}>
                  {debugCopied ? "Copied" : "Copy debug details"}
                </Button>
              </div>
              <p className="mt-2 text-xs text-[var(--pr-text-muted)]">
                Copy and paste this block so we can trace the exact API failure.
              </p>
              <textarea
                readOnly
                value={debugMessage}
                rows={12}
                className="mt-2 w-full rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-2 font-mono text-xs"
              />
            </Card>
          ) : null}
        </div>
      ) : null}

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-32" />
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
        </div>
      ) : (
        <div className="space-y-4">
          {isDietitian ? (
            <Card>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-base font-semibold">Pending member requests</h3>
                {hiddenPendingCount > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="ghost"
                      className="min-h-10 border border-[var(--pr-border)] px-3 text-xs"
                      onClick={() => setShowHiddenPending((current) => !current)}
                    >
                      {showHiddenPending
                        ? `Hide hidden (${hiddenPendingCount})`
                        : `Show hidden (${hiddenPendingCount})`}
                    </Button>
                    <Button
                      variant="ghost"
                      className="min-h-10 border border-[var(--pr-border)] px-3 text-xs"
                      onClick={resetHiddenPendingVisits}
                    >
                      Reset hidden list
                    </Button>
                  </div>
                ) : null}
              </div>
              {actionablePendingVisits.length === 0 ? (
                <p className="mt-2 text-sm text-[var(--pr-text-muted)]">
                  {hiddenPendingCount > 0 && !showHiddenPending
                    ? `No visible pending requests. ${hiddenPendingCount} hidden as stale (already accepted elsewhere).`
                    : "No pending requests right now."}
                </p>
              ) : (
                <div className="mt-3 space-y-3">
                  {actionablePendingVisits.map((visit) => {
                    const slots = Array.isArray(visit.preferred_slots) ? visit.preferred_slots : [];
                    const selectedIndex = selectedPendingSlot[visit.id] ?? 0;
                    const selectedSlot = slots[selectedIndex] ?? null;
                    return (
                      <article
                        key={`pending-${visit.id}`}
                        className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-3"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <p className="text-sm font-semibold">
                              {visit.type || "Visit"} request
                            </p>
                            <p className="text-xs text-[var(--pr-text-muted)]">
                              Requested {formatDate(visit.requested_start_at)} {visit.location ? `· ${visit.location}` : ""}
                            </p>
                          </div>
                          <span
                            className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${toStatusBadgeClass(
                              visit.status,
                            )}`}
                          >
                            {toStatusLabel(visit.status)}
                          </span>
                        </div>
                        {visit.notes ? <p className="mt-2 text-sm text-[var(--pr-text-muted)]">{visit.notes}</p> : null}

                        {slots.length > 0 ? (
                          <div className="mt-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">
                              Preferred slots
                            </p>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {slots.map((slot, slotIndex) => (
                                <button
                                  key={`${visit.id}-slot-${slotIndex}`}
                                  type="button"
                                  onClick={() =>
                                    setSelectedPendingSlot((current) => ({ ...current, [visit.id]: slotIndex }))
                                  }
                                  className={`rounded-xl border px-3 py-2 text-xs font-semibold transition ${
                                    selectedIndex === slotIndex
                                      ? "border-[color-mix(in_srgb,var(--pr-accent)_45%,transparent)] bg-[color-mix(in_srgb,var(--pr-accent)_14%,transparent)]"
                                      : "border-[var(--pr-border)] bg-[var(--pr-surface)] text-[var(--pr-text-muted)]"
                                  }`}
                                >
                                  {formatDate(slot.start_at)}
                                </button>
                              ))}
                            </div>
                            {selectedSlot ? (
                              <p className="mt-2 text-xs text-[var(--pr-text-muted)]">
                                Selected: {formatDate(withSlotFallback(selectedSlot).start)} to{" "}
                                {formatDate(withSlotFallback(selectedSlot).end)}
                              </p>
                            ) : null}
                          </div>
                        ) : null}

                        <div className="mt-3 flex flex-wrap gap-2">
                          <Button
                            variant="secondary"
                            className="min-h-10 px-3 text-sm"
                            disabled={updatingVisitId === visit.id}
                            onClick={() => void handleAcceptPending(visit)}
                          >
                            Accept
                          </Button>
                          <Button
                            variant="secondary"
                            className="min-h-10 px-3 text-sm"
                            disabled={updatingVisitId === visit.id}
                            onClick={() => void handleRejectPendingSlot(visit)}
                          >
                            Reject selected slot
                          </Button>
                          <Button
                            variant="ghost"
                            className="min-h-10 border border-[var(--pr-border)] px-3 text-sm text-rose-600"
                            disabled={updatingVisitId === visit.id}
                            onClick={() => void handleCancelPending(visit)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </Card>
          ) : null}

          {isDietitian ? (
            <>
              <Card>
                <h3 className="text-base font-semibold">Appointment status board</h3>
                <p className="mt-1 text-sm text-[var(--pr-text-muted)]">
                  Track every request by lifecycle stage and jump directly to the queue you need to process.
                </p>
                <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {(["ALL", "REQUESTED", "RESCHEDULED", "CONFIRMED", "COMPLETED", "CANCELED"] as const).map((statusKey) => {
                    const isActive = dietitianStatusFilter === statusKey;
                    return (
                      <button
                        key={`status-${statusKey}`}
                        type="button"
                        onClick={() => setDietitianStatusFilter(statusKey)}
                        className={`rounded-xl border px-3 py-3 text-left transition ${
                          isActive
                            ? "border-[color-mix(in_srgb,var(--pr-accent)_45%,transparent)] bg-[color-mix(in_srgb,var(--pr-accent)_14%,transparent)]"
                            : "border-[var(--pr-border)] bg-[var(--pr-surface-secondary)]"
                        }`}
                      >
                        <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">
                          {toStatusGroupLabel(statusKey)}
                        </p>
                        <p className="mt-1 text-lg font-semibold">{dietitianStatusCounts[statusKey]}</p>
                      </button>
                    );
                  })}
                </div>
              </Card>

              <Card>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <h3 className="text-base font-semibold">Appointment management</h3>
                    <p className="mt-1 text-xs text-[var(--pr-text-muted)]">
                      {dietitianManagedVisits.length} shown · Filter: {toStatusGroupLabel(dietitianStatusFilter)}
                    </p>
                  </div>
                </div>

                <div className="mt-3 grid gap-2 md:grid-cols-[180px_minmax(0,1fr)]">
                  <label className="flex flex-col gap-1 text-xs font-medium text-[var(--pr-text-muted)]">
                    Status queue
                    <select
                      value={dietitianStatusFilter}
                      onChange={(event) =>
                        setDietitianStatusFilter((event.target.value as DietitianStatusFilter) || "ALL")
                      }
                      className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-2 text-sm"
                    >
                      <option value="ALL">All</option>
                      <option value="REQUESTED">Requested</option>
                      <option value="RESCHEDULED">Rescheduled</option>
                      <option value="CONFIRMED">Confirmed</option>
                      <option value="COMPLETED">Completed</option>
                      <option value="CANCELED">Canceled</option>
                    </select>
                  </label>
                  <label className="flex flex-col gap-1 text-xs font-medium text-[var(--pr-text-muted)]">
                    Search appointments
                    <input
                      value={dietitianSearch}
                      onChange={(event) => setDietitianSearch(event.target.value)}
                      placeholder="Search by member id, visit id, type, notes, or location"
                      className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-2 text-sm"
                    />
                  </label>
                </div>

                {dietitianManagedVisits.length === 0 ? (
                  <p className="mt-3 text-sm text-[var(--pr-text-muted)]">
                    No appointments match the selected filter.
                  </p>
                ) : (
                  <div className="mt-3 space-y-3">
                    {dietitianManagedVisits.map((visit) => {
                      const draft = drafts[visit.id] ?? buildDraft(visit);
                      const normalizedStatus = toNormalizedVisitStatus(visit.status);
                      const isCompletedLocked = normalizedStatus === "COMPLETED";
                      const slots = Array.isArray(visit.preferred_slots) ? visit.preferred_slots : [];
                      const selectedIndex =
                        selectedPendingSlot[visit.id] ??
                        (typeof visit.accepted_slot_index === "number" ? visit.accepted_slot_index : 0);
                      const selectedSlot = slots[selectedIndex] ?? null;
                      const showsRescheduleSlots =
                        !isCompletedLocked &&
                        (normalizedStatus === "REQUESTED" || normalizedStatus === "RESCHEDULED") &&
                        slots.length > 0;
                      const acceptanceHistory = buildAcceptanceHistoryText({
                        visit,
                        currentDietitianId,
                        currentDietitianFirstName,
                      });
                      return (
                        <article
                          key={`managed-${visit.id}`}
                          className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-3"
                        >
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div>
                              <p className="text-sm font-semibold">
                                {visit.type || "Visit"} · {formatDate(visit.confirmed_start_at || visit.requested_start_at)}
                              </p>
                              <p className="text-xs text-[var(--pr-text-muted)]">
                                Member {visit.user_id || "unknown"} · Request {visit.id}
                              </p>
                              {acceptanceHistory ? (
                                <p className="mt-1 text-xs text-[var(--pr-text-muted)]">{acceptanceHistory}</p>
                              ) : null}
                              {visit.cancellation_reason ? (
                                <p className="mt-1 text-xs text-rose-600">Cancellation reason: {visit.cancellation_reason}</p>
                              ) : null}
                              {isCompletedLocked ? (
                                <p className="mt-1 text-xs font-semibold text-teal-700">
                                  Completed appointment. This record is read-only.
                                </p>
                              ) : null}
                            </div>
                            <span
                              className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${toStatusBadgeClass(
                                normalizedStatus,
                              )}`}
                            >
                              {toStatusLabel(normalizedStatus)}
                            </span>
                          </div>

                          {showsRescheduleSlots ? (
                            <div className="mt-3 rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-3">
                              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">
                                {normalizedStatus === "RESCHEDULED" ? "Updated preferred slots" : "Preferred slots"}
                              </p>
                              <div className="mt-2 flex flex-wrap gap-2">
                                {slots.map((slot, slotIndex) => (
                                  <button
                                    key={`${visit.id}-managed-slot-${slotIndex}`}
                                    type="button"
                                    onClick={() =>
                                      setSelectedPendingSlot((current) => ({ ...current, [visit.id]: slotIndex }))
                                    }
                                    className={`rounded-xl border px-3 py-2 text-xs font-semibold transition ${
                                      selectedIndex === slotIndex
                                        ? "border-[color-mix(in_srgb,var(--pr-accent)_45%,transparent)] bg-[color-mix(in_srgb,var(--pr-accent)_14%,transparent)]"
                                        : "border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] text-[var(--pr-text-muted)]"
                                    }`}
                                  >
                                    {formatDate(slot.start_at)}
                                  </button>
                                ))}
                              </div>
                              {selectedSlot ? (
                                <p className="mt-2 text-xs text-[var(--pr-text-muted)]">
                                  Selected: {formatDate(withSlotFallback(selectedSlot).start)} to{" "}
                                  {formatDate(withSlotFallback(selectedSlot).end)}
                                </p>
                              ) : null}
                              <div className="mt-3 flex flex-wrap gap-2">
                                <Button
                                  variant="secondary"
                                  className="min-h-10 px-3 text-sm"
                                  disabled={updatingVisitId === visit.id}
                                  onClick={() => void handleAcceptPending(visit)}
                                >
                                  Accept selected slot
                                </Button>
                                <Button
                                  variant="secondary"
                                  className="min-h-10 px-3 text-sm"
                                  disabled={updatingVisitId === visit.id}
                                  onClick={() => void handleRejectPendingSlot(visit)}
                                >
                                  Reject selected slot
                                </Button>
                                <Button
                                  variant="ghost"
                                  className="min-h-10 border border-[var(--pr-border)] px-3 text-sm text-rose-600"
                                  disabled={updatingVisitId === visit.id}
                                  onClick={() => void handleCancelPending(visit)}
                                >
                                  Cancel request
                                </Button>
                              </div>
                            </div>
                          ) : null}

                          <div className="mt-3 grid gap-2 lg:grid-cols-2">
                            <label className="flex flex-col gap-1 text-xs font-medium text-[var(--pr-text-muted)]">
                              Status
                              <select
                                value={draft.status}
                                onChange={(event) =>
                                  patchDraft(visit.id, { status: (event.target.value as VisitStatus) || "REQUESTED" })
                                }
                                disabled={isCompletedLocked}
                                className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-2 text-sm"
                              >
                                <option value="REQUESTED">Requested</option>
                                <option value="CONFIRMED">Confirmed</option>
                                <option value="RESCHEDULED">Rescheduled</option>
                                <option value="CANCELED">Canceled</option>
                                <option value="COMPLETED">Completed</option>
                              </select>
                            </label>
                            <label className="flex flex-col gap-1 text-xs font-medium text-[var(--pr-text-muted)]">
                              Location
                              <input
                                value={draft.location}
                                onChange={(event) => patchDraft(visit.id, { location: event.target.value })}
                                disabled={isCompletedLocked}
                                className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-2 text-sm"
                              />
                            </label>
                            <label className="flex flex-col gap-1 text-xs font-medium text-[var(--pr-text-muted)]">
                              Confirmed start
                              <input
                                type="datetime-local"
                                value={draft.confirmedStartAt}
                                onChange={(event) => patchDraft(visit.id, { confirmedStartAt: event.target.value })}
                                disabled={isCompletedLocked}
                                className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-2 text-sm"
                              />
                            </label>
                            <label className="flex flex-col gap-1 text-xs font-medium text-[var(--pr-text-muted)]">
                              Confirmed end
                              <input
                                type="datetime-local"
                                value={draft.confirmedEndAt}
                                onChange={(event) => patchDraft(visit.id, { confirmedEndAt: event.target.value })}
                                disabled={isCompletedLocked}
                                className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-2 text-sm"
                              />
                            </label>
                            <label className="flex flex-col gap-1 text-xs font-medium text-[var(--pr-text-muted)] lg:col-span-2">
                              Notes
                              <textarea
                                rows={2}
                                value={draft.notes}
                                onChange={(event) => patchDraft(visit.id, { notes: event.target.value })}
                                disabled={isCompletedLocked}
                                className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-2 text-sm"
                              />
                            </label>
                            <label className="flex flex-col gap-1 text-xs font-medium text-[var(--pr-text-muted)]">
                              Zoom link
                              <input
                                value={draft.zoomLink}
                                onChange={(event) => patchDraft(visit.id, { zoomLink: event.target.value })}
                                placeholder="https://zoom.us/j/..."
                                disabled={isCompletedLocked}
                                className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-2 text-sm"
                              />
                            </label>
                            <label className="flex flex-col gap-1 text-xs font-medium text-[var(--pr-text-muted)]">
                              Meeting metadata
                              <input
                                value={draft.meetingMetadata}
                                onChange={(event) => patchDraft(visit.id, { meetingMetadata: event.target.value })}
                                placeholder="Call agenda or room details"
                                disabled={isCompletedLocked}
                                className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-2 text-sm"
                              />
                            </label>
                            {draft.status === "CANCELED" ? (
                              <label className="flex flex-col gap-1 text-xs font-medium text-[var(--pr-text-muted)] lg:col-span-2">
                                Cancellation reason
                                <input
                                  value={draft.cancellationReason}
                                  onChange={(event) => patchDraft(visit.id, { cancellationReason: event.target.value })}
                                  disabled={isCompletedLocked}
                                  className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-2 text-sm"
                                />
                              </label>
                            ) : null}
                            <div className="flex justify-end gap-2 lg:col-span-2">
                              {draft.zoomLink ? (
                                <a
                                  href={draft.zoomLink}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="pr-btn-secondary inline-flex min-h-10 items-center px-3 text-sm font-semibold"
                                >
                                  Start Meeting
                                </a>
                              ) : null}
                              <Button
                                variant="secondary"
                                className="min-h-10 px-3 text-sm"
                                disabled={updatingVisitId === visit.id || isCompletedLocked}
                                onClick={() => void handleSaveVisit(visit)}
                              >
                                {isCompletedLocked
                                  ? "Completed"
                                  : updatingVisitId === visit.id
                                    ? "Saving..."
                                    : "Save updates"}
                              </Button>
                            </div>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )}
              </Card>
            </>
          ) : (
            <>
              {memberVisits.length === 0 ? (
                <Card>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <h3 className="text-base font-semibold">My appointments</h3>
                      <p className="mt-1 text-xs text-[var(--pr-text-muted)]">
                        Swipe-style focus deck for active appointments, with archive kept out of the way.
                      </p>
                    </div>
                    <span className="rounded-full border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-1 text-xs font-semibold">
                      0 total
                    </span>
                  </div>
                  <div className="mt-4">
                    <EmptyState
                      title="No appointments yet"
                      subtitle="Create your first request from the schedule button below."
                    />
                  </div>
                </Card>
              ) : (
                <Card>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h3 className="text-base font-semibold">My appointments</h3>
                  <p className="mt-1 text-xs text-[var(--pr-text-muted)]">
                    Swipe-style focus deck for active appointments, with archive kept out of the way.
                  </p>
                </div>
                <span className="rounded-full border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-1 text-xs font-semibold">
                  {memberVisits.length} total
                </span>
              </div>

              <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                {([
                  { lane: "FOCUS", label: "Focus deck", count: memberFocusVisits.length },
                  { lane: "UPCOMING", label: "Upcoming", count: memberUpcomingVisits.length },
                  { lane: "COMPLETED", label: "Completed", count: memberCompletedVisits.length },
                  { lane: "CANCELED", label: "Canceled", count: memberCanceledVisits.length },
                ] as const).map((lane) => {
                  const isActive = memberLane === lane.lane;
                  return (
                    <button
                      key={`member-lane-${lane.lane}`}
                      type="button"
                      onClick={() => {
                        setMemberLane(lane.lane);
                        setMemberDeckIndex(0);
                      }}
                      className={`rounded-xl border px-3 py-3 text-left transition ${
                        isActive
                          ? "border-[color-mix(in_srgb,var(--pr-accent)_45%,transparent)] bg-[color-mix(in_srgb,var(--pr-accent)_14%,transparent)]"
                          : "border-[var(--pr-border)] bg-[var(--pr-surface-secondary)]"
                      }`}
                    >
                      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">{lane.label}</p>
                      <p className="mt-1 text-lg font-semibold">{lane.count}</p>
                    </button>
                  );
                })}
              </div>

              {memberLaneVisits.length === 0 ? (
                <p className="mt-4 text-sm text-[var(--pr-text-muted)]">No appointments in this lane.</p>
              ) : (
                <div className="mt-4 space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-[var(--pr-text-muted)]">
                    <p>
                      Card {Math.min(memberDeckIndex + 1, memberLaneVisits.length)} of {memberLaneVisits.length}
                    </p>
                    {memberLaneVisits.length > 1 ? (
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          className="min-h-10 border border-[var(--pr-border)] px-3 text-xs"
                          onClick={() =>
                            setMemberDeckIndex((current) =>
                              current === 0 ? memberLaneVisits.length - 1 : current - 1,
                            )
                          }
                        >
                          Previous
                        </Button>
                        <Button
                          variant="ghost"
                          className="min-h-10 border border-[var(--pr-border)] px-3 text-xs"
                          onClick={() =>
                            setMemberDeckIndex((current) =>
                              current >= memberLaneVisits.length - 1 ? 0 : current + 1,
                            )
                          }
                        >
                          Next
                        </Button>
                      </div>
                    ) : null}
                  </div>

                  {activeMemberVisit && activeMemberDraft ? (
                    <div className="relative pt-2">
                      <div className="pointer-events-none absolute inset-x-4 top-4 h-full rounded-2xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] opacity-60" />
                      <div className="pointer-events-none absolute inset-x-2 top-2 h-full rounded-2xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] opacity-80" />
                      <article className="relative rounded-2xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-4 py-4 shadow-card">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <p className="text-sm font-semibold">
                              {activeMemberVisit.type || "Visit"} ·{" "}
                              {formatDate(activeMemberVisit.confirmed_start_at || activeMemberVisit.requested_start_at)}
                            </p>
                            <p className="text-xs text-[var(--pr-text-muted)]">
                              {activeMemberVisit.notes || "Your dietitian will confirm timing and add visit notes here."}
                            </p>
                            {activeMemberVisit.cancellation_reason ? (
                              <p className="mt-1 text-xs text-rose-600">
                                Cancellation reason: {activeMemberVisit.cancellation_reason}
                              </p>
                            ) : null}
                          </div>
                          <span
                            className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${toStatusBadgeClass(
                              activeMemberStatus || activeMemberVisit.status,
                            )}`}
                          >
                            {toStatusLabel(activeMemberStatus || activeMemberVisit.status)}
                          </span>
                        </div>

                        {!canMemberManageAppointments ? (
                          <p className="mt-3 text-xs font-semibold text-rose-600">
                            You need at least 1 dietitian credit to manage appointments.
                          </p>
                        ) : null}

                        {activeMemberStatus === "CONFIRMED" ? (
                          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-[var(--pr-text-muted)]">
                            <span>
                              Confirmed window: {formatDate(activeMemberVisit.confirmed_start_at)} to{" "}
                              {formatDate(activeMemberVisit.confirmed_end_at)}
                            </span>
                            {activeMemberVisit.meeting_url ? (
                              <a
                                href={activeMemberVisit.meeting_url}
                                target="_blank"
                                rel="noreferrer"
                                className="pr-btn-secondary inline-flex min-h-10 items-center px-3 text-sm font-semibold"
                              >
                                Start Meeting
                              </a>
                            ) : null}
                          </div>
                        ) : null}

                        {!activeMemberIsReadOnly ? (
                          <div className="mt-3 space-y-3">
                            <div className="flex flex-wrap gap-2">
                              <Button
                                variant="ghost"
                                className="min-h-10 border border-[var(--pr-border)] px-3 text-sm"
                                disabled={activeMemberIsMutating || !canMemberManageAppointments}
                                onClick={() => toggleMemberActions(activeMemberVisit.id)}
                              >
                                {activeMemberActionsExpanded ? "Hide reschedule" : "Reschedule"}
                              </Button>
                              <Button
                                variant="ghost"
                                className="min-h-10 border border-[var(--pr-border)] px-3 text-sm text-rose-600"
                                disabled={activeMemberIsMutating || !canMemberManageAppointments}
                                onClick={() => void handleMemberCancel(activeMemberVisit)}
                              >
                                Cancel appointment
                              </Button>
                            </div>

                            {activeMemberActionsExpanded ? (
                              <div className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-3">
                                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">
                                  Request new preferred slots
                                </p>
                                <div className="mt-2 grid gap-2 md:grid-cols-2">
                                  {Array.from({ length: 5 }, (_, slotIndex) => (
                                    <label
                                      key={`${activeMemberVisit.id}-member-slot-${slotIndex}`}
                                      className="flex flex-col gap-1 text-xs font-medium text-[var(--pr-text-muted)]"
                                    >
                                      Preferred slot {slotIndex + 1}{slotIndex >= 3 ? " (optional)" : ""}
                                      <input
                                        type="datetime-local"
                                        value={activeMemberDraft.preferredSlots[slotIndex] || ""}
                                        onChange={(event) =>
                                          patchMemberDraft(activeMemberVisit.id, slotIndex, event.target.value)
                                        }
                                        disabled={activeMemberIsMutating}
                                        className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-2 text-sm"
                                      />
                                    </label>
                                  ))}
                                </div>
                                <div className="mt-3">
                                  <Button
                                    variant="secondary"
                                    className="min-h-10 px-3 text-sm"
                                    disabled={activeMemberIsMutating || !canMemberManageAppointments}
                                    onClick={() => void handleMemberReschedule(activeMemberVisit)}
                                  >
                                    {activeMemberIsMutating ? "Submitting..." : "Submit reschedule request"}
                                  </Button>
                                </div>
                              </div>
                            ) : null}
                          </div>
                        ) : (
                          <p className="mt-3 text-xs text-[var(--pr-text-muted)]">
                            {activeMemberStatus === "COMPLETED"
                              ? "This appointment is completed and read-only."
                              : "This appointment is canceled."}
                          </p>
                        )}
                      </article>
                    </div>
                  ) : null}
                </div>
              )}

              {memberArchiveVisits.length > 0 ? (
                <details className="mt-4 rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2">
                  <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">
                    Timeline archive ({memberArchiveVisits.length})
                  </summary>
                  <div className="mt-2 space-y-2">
                    {memberArchiveVisits.slice(0, 8).map((visit) => (
                      <div
                        key={`archive-${visit.id}`}
                        className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-2"
                      >
                        <p className="text-xs font-medium">
                          {visit.type || "Visit"} · {formatDate(visit.confirmed_start_at || visit.requested_start_at)}
                        </p>
                        <span
                          className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${toStatusBadgeClass(
                            visit.status,
                          )}`}
                        >
                          {toStatusLabel(visit.status)}
                        </span>
                      </div>
                    ))}
                  </div>
                </details>
              ) : null}
            </Card>

              )}

              <Card>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <h3 className="text-base font-semibold">Schedule a visit</h3>
                    <p className="mt-1 text-xs text-[var(--pr-text-muted)]">
                      Open the scheduler when you want to submit a new appointment request.
                    </p>
                  </div>
                  <Button
                    variant="secondary"
                    className="min-h-10 px-3 text-sm"
                    type="button"
                    onClick={() => setShowMemberScheduler((current) => !current)}
                  >
                    {showMemberScheduler ? "Hide scheduler" : "Schedule visit"}
                  </Button>
                </div>

                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  <div className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2 text-xs">
                    Dietitian credits:{" "}
                    <span className="font-semibold text-[var(--pr-text)]">
                      {dietitianCredits === null ? "-" : dietitianCredits}
                    </span>
                  </div>
                  <div className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2 text-xs">
                    Scan credits:{" "}
                    <span className="font-semibold text-[var(--pr-text)]">{scanCredits === null ? "-" : scanCredits}</span>
                  </div>
                </div>

                {!canMemberManageAppointments ? (
                  <p className="mt-3 text-xs font-semibold text-rose-600">
                    At least 1 dietitian credit is required to book or manage appointments.
                  </p>
                ) : null}

                {showMemberScheduler ? (
                  <form className="mt-3 grid gap-3 lg:grid-cols-2" onSubmit={(event) => void handleCreateRequest(event)}>
                    <label className="flex flex-col gap-2 text-sm">
                      <span className="font-medium text-[var(--pr-text-muted)]">Visit type</span>
                      <select
                        value={visitType}
                        onChange={(event) => setVisitType((event.target.value as VisitType) || "VIDEO")}
                        className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2"
                      >
                        <option value="VIDEO">Video</option>
                        <option value="IN_PERSON">In person</option>
                      </select>
                    </label>
                    <label className="flex flex-col gap-2 text-sm">
                      <span className="font-medium text-[var(--pr-text-muted)]">Requested start</span>
                      <input
                        type="datetime-local"
                        value={startAt}
                        onChange={(event) => setStartAt(event.target.value)}
                        className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2"
                      />
                    </label>
                    <label className="flex flex-col gap-2 text-sm">
                      <span className="font-medium text-[var(--pr-text-muted)]">Requested end</span>
                      <input
                        type="datetime-local"
                        value={endAt}
                        onChange={(event) => setEndAt(event.target.value)}
                        className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2"
                      />
                    </label>
                    <label className="flex flex-col gap-2 text-sm">
                      <span className="font-medium text-[var(--pr-text-muted)]">Preferred slot 1</span>
                      <input
                        type="datetime-local"
                        value={preferredSlot1}
                        onChange={(event) => setPreferredSlot1(event.target.value)}
                        className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2"
                      />
                    </label>
                    <label className="flex flex-col gap-2 text-sm">
                      <span className="font-medium text-[var(--pr-text-muted)]">Preferred slot 2</span>
                      <input
                        type="datetime-local"
                        value={preferredSlot2}
                        onChange={(event) => setPreferredSlot2(event.target.value)}
                        className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2"
                      />
                    </label>
                    <label className="flex flex-col gap-2 text-sm">
                      <span className="font-medium text-[var(--pr-text-muted)]">Preferred slot 3</span>
                      <input
                        type="datetime-local"
                        value={preferredSlot3}
                        onChange={(event) => setPreferredSlot3(event.target.value)}
                        className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2"
                      />
                    </label>
                    <label className="flex flex-col gap-2 text-sm">
                      <span className="font-medium text-[var(--pr-text-muted)]">Preferred slot 4 (optional)</span>
                      <input
                        type="datetime-local"
                        value={preferredSlot4}
                        onChange={(event) => setPreferredSlot4(event.target.value)}
                        className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2"
                      />
                    </label>
                    <label className="flex flex-col gap-2 text-sm lg:col-span-2">
                      <span className="font-medium text-[var(--pr-text-muted)]">Preferred slot 5 (optional)</span>
                      <input
                        type="datetime-local"
                        value={preferredSlot5}
                        onChange={(event) => setPreferredSlot5(event.target.value)}
                        className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2"
                      />
                    </label>
                    <label className="flex flex-col gap-2 text-sm lg:col-span-2">
                      <span className="font-medium text-[var(--pr-text-muted)]">Location / notes</span>
                      <input
                        value={location}
                        onChange={(event) => setLocation(event.target.value)}
                        placeholder="Clinic address or video link preference"
                        className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2"
                      />
                      <textarea
                        rows={2}
                        value={notes}
                        onChange={(event) => setNotes(event.target.value)}
                        placeholder="Add context for the request..."
                        className="rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2"
                      />
                    </label>
                    <div className="flex justify-end lg:col-span-2">
                      <Button type="submit" disabled={requesting || !canMemberManageAppointments}>
                        {requesting ? "Submitting..." : "Request appointment"}
                      </Button>
                    </div>
                  </form>
                ) : (
                  <p className="mt-3 text-sm text-[var(--pr-text-muted)]">
                    Click "Schedule visit" to add preferred time slots and request a new appointment.
                  </p>
                )}
              </Card>
            </>
          )}
        </div>
      )}
    </div>
  );
}
